// main-best-hotels.component.ts
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  HostListener,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import {HotelGalleryComponent} from '../../../../elements/hotel-gallery/hotel-gallery.component';
import {TextsComponent} from '../../../../elements/texts/texts.component';
import {Text} from '../../../../../model/text';
import {Location, NgIf} from '@angular/common';
import {Icons} from '../../../../../model/icon';
import {text} from '../../../../../functions/localization';
import {Card} from '../../../../../model/cards';
import {ThemeService} from '../../../../../services/theme.service';
import {FormsModule} from '@angular/forms';
import {environment} from '../../../../../environments/environment';
import {HotelDtoService} from '../../../../../services/hotel-dto-service';
import {PaginationParams} from '../../../../../model/pagination';
import {getFirstSentence} from '../../../../../functions/getFirstSentence';
import {Subscription} from 'rxjs';
import {LazyLoadService} from '../../../../../services/lazy-load.service';

@Component({
  selector: 'app-main-best-hotels',
  imports: [
    HotelGalleryComponent,
    TextsComponent,
    NgIf,
    FormsModule,
  ],
  templateUrl: './main-best-hotels.component.html',
  standalone: true,
  styleUrl: './main-best-hotels.component.scss'
})
export class MainBestHotelsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  constructor(
    private hotelService: HotelDtoService,
    public themeService: ThemeService,
    private elementRef: ElementRef,
    public location: Location,
    private lazyLoadService: LazyLoadService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() texts: Text[] = [];
  @Input() title: boolean = true;
  @Input() subtitle: boolean = false;
  @Input() search: boolean = false;
  @Input() arrows: boolean = false;
  @Input() filters: boolean = false;
  @Input() main: boolean = true;
  @Input() subtitleText: string[] = ['', ''];
  @Input() cards: Card[] = [];
  @Input() type: 'continent' | 'country' | 'city' | undefined = undefined;
  @Input() typeContent: string | undefined = undefined;
  @Input() random: boolean = true;
  @Input() paddingLess: boolean = false;
  @Output() heightChange = new EventEmitter<number>();

  firstArray: Card[] = [];
  secondArray: Card[] = [];
  inputIsEmpty: boolean = true;
  searchInputValue: string = '';
  hotels: Card[] = [];
  filteredHotels: Card[] = [];
  filterNull: boolean = false;
  screenWidth: number = window.innerWidth;
  private heightEmissionTimeout: any;
  private initialCardsLoaded: boolean = false;
  private hotelsListLoaded: boolean = false;

  lazyLoadSubscription: Subscription = new Subscription();
  allHotelsLoaded: boolean = false;
  isLoadingMore: boolean = false;

  private firstSliderComponent: any;
  private secondSliderComponent: any;
  private firstSliderScrollLeft: number = 0;
  private secondSliderScrollLeft: number = 0;

  private isLazyLoadingMode: boolean = false;

  private allLoadedCards: Card[] = [];

  hotelList: PaginationParams = new PaginationParams({
    query: '',
    pageSize: 10,
    pageNumber: 0,
    sort: 'rating:asc',
    cityId: '',
    countryId: '',
    continentId: '',
  });

  margin1: string = 'margin-top: 100px;'
  margin2: string = 'margin-top: 75px;'

  isMobile: boolean = false;

  ngOnInit() {
    if (this.location.path() === '') {
      this.isLazyLoadingMode = true;
      this.setupLazyLoading();
      this.loadInitialHotels();
    }
    this.initialCardsLoaded = true;
    this.updateDisplayedCards();
    if (window.innerWidth < 571) {
      this.isMobile = true;
      this.margin1 = 'margin-top: 40px;';
      this.margin2 = '';
    }
    if (this.search) {
      this.setType();
      this.getFullHotelList();
    }
  }

  private setupLazyLoading() {
    this.lazyLoadSubscription.add(
      this.lazyLoadService.newCards$.subscribe((newCards) => {
        this.saveSliderPositions();
        this.allLoadedCards = [...this.allLoadedCards, ...newCards];
        let cardsToAdd = newCards;
        if (this.random) {
          cardsToAdd = this.shuffleArray([...newCards]);
        }
        this.cards = [...this.cards, ...cardsToAdd];
        if (this.inputIsEmpty) {
          this.updateDisplayedCardsPreservingOrder();
          this.restoreSliderPositions();
          this.scheduleHeightEmission();
        }
        this.cdr.detectChanges();
      })
    );

    this.lazyLoadSubscription.add(
      this.lazyLoadService.loading$.subscribe((isLoading) => {
        this.isLoadingMore = isLoading;
        this.cdr.detectChanges();
      })
    );

    this.lazyLoadSubscription.add(
      this.lazyLoadService.allLoaded$.subscribe(() => {
        this.allHotelsLoaded = true;
      })
    );
  }

  private loadInitialHotels() {
    this.lazyLoadService.reset();
    this.allLoadedCards = [];
    this.cards = [];
    this.lazyLoadService.loadNextBatch();
  }

  onSliderEndReached() {
    if (this.location.path() === '' &&
      this.inputIsEmpty &&
      !this.isLoadingMore &&
      !this.allHotelsLoaded) {
      this.lazyLoadService.loadNextBatch();
    }
  }

  registerFirstSlider(slider: any) {
    this.firstSliderComponent = slider;
  }

  registerSecondSlider(slider: any) {
    this.secondSliderComponent = slider;
  }

  private saveSliderPositions() {
    if (this.firstSliderComponent?.slider?.nativeElement) {
      this.firstSliderScrollLeft = this.firstSliderComponent.slider.nativeElement.scrollLeft;
    }
    if (this.secondSliderComponent?.slider?.nativeElement) {
      this.secondSliderScrollLeft = this.secondSliderComponent.slider.nativeElement.scrollLeft;
    }
  }

  private restoreSliderPositions() {
    setTimeout(() => {
      if (this.firstSliderComponent?.slider?.nativeElement) {
        this.firstSliderComponent.slider.nativeElement.scrollLeft = this.firstSliderScrollLeft;
      }
      if (this.secondSliderComponent?.slider?.nativeElement) {
        this.secondSliderComponent.slider.nativeElement.scrollLeft = this.secondSliderScrollLeft;
      }
    }, 0);
  }

  setType() {
    if (this.type !== undefined && this.typeContent !== undefined) {
      switch (this.type) {
        case 'continent':
          this.hotelList.continentId = this.typeContent;
          break;
        case 'country':
          this.hotelList.countryId = this.typeContent;
          break;
        case 'city':
          this.hotelList.cityId = this.typeContent;
          break;
      }
    }
  }

  ngAfterViewInit() {
    this.scheduleHeightEmission();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['cards'] && !changes['cards'].firstChange) {
      if (this.inputIsEmpty) {
        this.updateDisplayedCards();
        this.scheduleHeightEmission();
      }
    }
    if ((changes['typeContent'] || changes['type']) && this.search) {
      this.setType();
      this.getFullHotelList();
    }
    if (changes['random'] && this.inputIsEmpty) {
      this.updateDisplayedCards();
      this.scheduleHeightEmission();
    }
  }

  ngOnDestroy() {
    if (this.heightEmissionTimeout) {
      clearTimeout(this.heightEmissionTimeout);
    }
    if (this.lazyLoadSubscription) {
      this.lazyLoadSubscription.unsubscribe();
    }
  }

  get paddingStyle() {
    if (!this.isMobile) {
      return '';
    }
    if (this.location.path() !== ''){
      return '';
    }
    return '23px'
  }

  emitHeight() {
    const nativeElement = this.elementRef.nativeElement as HTMLElement;
    const style = getComputedStyle(nativeElement);
    const offsetHeight = nativeElement.offsetHeight;
    const marginTop = parseFloat(style.marginTop) || 0;
    const marginBottom = parseFloat(style.marginBottom) || 0;
    const totalHeight = offsetHeight + marginTop + marginBottom;
    this.heightChange.emit(totalHeight);
  }

  private scheduleHeightEmission() {
    if (this.heightEmissionTimeout) {
      clearTimeout(this.heightEmissionTimeout);
    }
    this.heightEmissionTimeout = setTimeout(() => {
      this.emitHeight();
    }, 50);
  }

  detectLanguage(text: string): 'ru' | 'en' {
    const firstChar = text.charAt(0);
    return /[а-яА-ЯёЁ]/.test(firstChar) ? 'ru' : 'en';
  }

  getPlaceHolder() {
    return text(['Search hotel...', 'Search hotel...']);
  }

  getCardsPerRow(): number {
    if (this.screenWidth < 576) {
      return 1;
    } else if (this.screenWidth < 769) {
      return 2;
    } else {
      return 3;
    }
  }

  getMaxCardsForSingleRow(): number {
    const cardsPerRow = this.getCardsPerRow();
    return cardsPerRow * 2 - 1;
  }

  private updateDisplayedCardsPreservingOrder() {
    const cardsToProcess = this.getCurrentCardList();
    this.splitArrayInternal(cardsToProcess);
  }

  private updateDisplayedCards() {
    this.saveSliderPositions();
    const cardsToProcess = this.getCurrentCardList();
    const shuffledCards = this.random ? this.shuffleArray([...cardsToProcess]) : cardsToProcess;
    this.splitArrayInternal(shuffledCards);

    this.restoreSliderPositions();
    this.scheduleHeightEmission();
  }

  private splitArrayInternal(cards: Card[]) {
    const cardsPerRow = this.getCardsPerRow();
    const maxCardsForSingleRow = this.getMaxCardsForSingleRow();

    if (cards.length <= maxCardsForSingleRow) {
      this.firstArray = [...cards];
      this.secondArray = [];
    } else {
      const firstRowCount = Math.ceil(cards.length / 2);
      this.firstArray = cards.slice(0, firstRowCount);
      this.secondArray = cards.slice(firstRowCount);
    }
  }

  splitArray(cards: Card[]) {
    this.splitArrayInternal(cards);
    this.scheduleHeightEmission();
  }

  getArray(param: string) {
    switch (param) {
      case 'first':
        return this.firstArray ?? []
      case 'second':
        return this.secondArray ?? []
      default: return []
    }
  }

  onSearchInputChange(value: string) {
    this.searchInputValue = value;
    this.inputIsEmpty = value.trim().length === 0;
    if (this.inputIsEmpty) {
      this.updateDisplayedCards();
      this.filterNull = false;
      this.scheduleHeightEmission();
      return;
    }
    const searchTerm = value.toLowerCase();
    const language = this.detectLanguage(searchTerm);
    this.filteredHotels = this.hotels.filter(hotel => {
      const titleToSearch = language === 'ru' ? hotel.text[0] : hotel.text[1];
      return titleToSearch.toLowerCase().includes(searchTerm);
    });
    const filteredCardsToProcess = this.random ?
      this.shuffleArray([...this.filteredHotels]) : this.filteredHotels;
    this.splitArrayInternal(filteredCardsToProcess);
    this.filterNull = this.filteredHotels.length === 0;
    this.scheduleHeightEmission();
  }

  private getCurrentCardList(): Card[] {
    if (!this.inputIsEmpty && this.filteredHotels.length > 0) {
      return this.filteredHotels;
    }
    return this.cards;
  }

  getFullHotelList() {
    this.hotelService.getHotelList(this.hotelList).subscribe((data) => {
      this.hotels = (data.content ?? []).map(v => {
        const path = v.image ? (environment.s3Prefix + v.image) : '/app/assets/images/hotel.png';
        let link: string | null;
        if (v.city?.country?.continent?.url?.length && v.city?.country?.url) {
          link = v.city?.country?.continent?.url + '/' + v.city?.country?.url + '/' +
            v.city?.url + '/' + v?.url;
        } else {
          link = null;
        }
        let descrRu = '';
        let descrEn = '';
        if (v.descriptionRu !== undefined) {
          descrRu = getFirstSentence(v.descriptionRu);
        }
        if (v.descriptionEn !== undefined) {
          descrEn = getFirstSentence(v.descriptionEn);
        }
        return new Card({
          title: ['', ''],
          text: [v.titleRu, v.titleEn],
          img: path,
          style: v.backgroundStyle?.toString() ?? '',
          link: link,
          rating: v.rating ?? 0,
          anotherButton: true,
          anotherHotelButton: true,
          anotherButtonText: [{text: [descrRu, descrEn]}],
        });
      });

      this.hotelsListLoaded = true;
      if (!this.inputIsEmpty && this.searchInputValue) {
        this.onSearchInputChange(this.searchInputValue);
      }
      this.scheduleHeightEmission();
    });
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  openWhatsApp() {
    const phoneNumber = '+66991609711';
    const message = 'тест';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    window.open(url, '_blank');
  }

  getStyle() {
    if (this.location.path() === '/services') {
      return 'padding-bottom: 0';
    }
    return '';
  }

  get cardsLength() {
    const secondLength = this.getArray('second').length;
    return secondLength === 0;
  }

  get firstText() {
    const x : Text[] = [{text: ['Пожалуйста, свяжитесь с нами', 'Please contact us'], fontFamily: 'pd', fontStyle: 'italic'}];
    const y : Text[] = [{text: ['Пожалуйста, свяжитесь с нами', 'Please contact us'], fontFamily: 'pd', fontStyle: 'italic'}];
    if (this.isMobile) {
      return y
    }
    return x
  }

  get secondText() {
    const x : Text[] = [
      {text: [' — мы подберём идеальный.', ', and we’ll find the perfect option for you.']},
    ];
    const y : Text[] = [
      {text: ['Мы подберём идеальный.', ', And we’ll find the perfect option for you.']},
    ];
    if (this.isMobile) {
      return y
    }
    return x
  }

  protected readonly Icons = Icons;
  protected readonly text = text;
}
